import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
} from '@azure/msal-browser';
import type { RuntimeConfig } from './config';

/**
 * Entra ID in the browser, against the showling-staff app registration.
 *
 * WHAT REPLACED WHAT. This used to be Cognito SRP against a separate STAFF
 * user pool, and the separation was cryptographic: a family token was signed by
 * keys the staff API had never heard of, so no amount of misconfiguration could
 * let a parent into the control plane. There is now ONE Entra tenant serving
 * both surfaces, so every token in the system is signed by the same keys and
 * two things stand between a parent and /control/admin:
 *
 *   1. the audience — this SPA's tokens are minted for showling-staff, and
 *      azure/functions/auth.py checks `aud` against STAFF_AUDIENCE;
 *   2. the `staff` app role, which auth.py refuses without (403, not 401).
 *
 * Entra also refuses one layer earlier: entra_bootstrap.sh sets
 * appRoleAssignmentRequired on the staff service principal, so an unassigned
 * user is turned away at sign-in with AADSTS50105 and never holds a staff token
 * at all. Both refusals reach the operator as words — see App.tsx.
 *
 * REDIRECT, NOT POPUP, and the boot order below is the whole reason this file
 * has a comment. `handleRedirectPromise()` must be awaited before anything asks
 * for accounts; skip it and a user returning from Entra with a code in the URL
 * fragment looks signed out, gets bounced to sign-in, and loops.
 */

let pca: PublicClientApplication | null = null;
let scope = '';

/**
 * What came back from Entra when the page loaded.
 *
 * `refused` is a first-class outcome rather than a thrown error because it is
 * not a bug and not transient: it is Entra saying this human may not use this
 * application. It gets a screen, not a red box.
 */
export type RedirectOutcome =
  | { kind: 'signed-in' }
  | { kind: 'none' }
  | { kind: 'refused'; message: string; unassigned: boolean };

export async function configureAuth(config: RuntimeConfig): Promise<RedirectOutcome> {
  scope = config.apiScope;
  pca = new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: config.authority,
      // PINNED, not defaulted. MSAL's default redirectUri is the current page,
      // and this console deep-links to /clips/<id>, /families/<id> and
      // /commissions/<id> — every one of which would have to be a registered
      // redirect URI or fail with AADSTS50011. entra_bootstrap.sh registers
      // ORIGINS (SHOWLING_ADMIN_ORIGINS), so the origin is the only URI that
      // is actually registered. navigateToLoginRequestUrl stays on (MSAL's
      // default), which is what returns the operator to the deep link they
      // started from after the round trip.
      redirectUri: `${window.location.origin}/`,
      postLogoutRedirectUri: `${window.location.origin}/`,
    },
    cache: {
      // Explicit, because this is a control plane with production spend behind
      // it: closing the tab ends the session. localStorage would keep a staff
      // token alive on a shared machine until it expired. Re-opening the
      // console is a silent redirect through Entra's own session, not a
      // password prompt, so the cost is a page load.
      cacheLocation: 'sessionStorage',
    },
  });

  await pca.initialize();

  try {
    const result = await pca.handleRedirectPromise();
    if (result?.account) {
      pca.setActiveAccount(result.account);
      return { kind: 'signed-in' };
    }
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    // AADSTS50105: "the signed-in user is not assigned to a role for the
    // application". This is the appRoleAssignmentRequired refusal, and it is
    // the SAME operator problem as the API's 403 — one fix, two places it can
    // surface, depending on whether Entra or the API says no first.
    return { kind: 'refused', message, unassigned: message.includes('AADSTS50105') };
  }

  const existing = pca.getAllAccounts();
  if (existing.length && existing[0]) {
    pca.setActiveAccount(existing[0]);
    return { kind: 'signed-in' };
  }
  return { kind: 'none' };
}

export function currentAccount(): AccountInfo | null {
  return pca?.getActiveAccount() ?? null;
}

/** The signed-in operator, for the header. `username` is the UPN. */
export function currentUserEmail(): string | null {
  const account = currentAccount();
  return account ? account.username || account.name || account.homeAccountId : null;
}

export async function startSignIn(): Promise<void> {
  if (!pca) throw new Error('configureAuth() was never called');
  // Navigates away; nothing after this runs. The scope is requested HERE as
  // well as on every silent acquisition so consent, if it is ever needed, is
  // asked for once at the front door rather than on the first API call.
  await pca.loginRedirect({ scopes: [scope] });
}

export async function endSession(): Promise<void> {
  if (!pca) return;
  await pca.logoutRedirect({ account: currentAccount() });
}

/**
 * An ACCESS token for the staff API — never the id token.
 *
 * The Cognito version of this file deliberately sent the ID token, because
 * HttpUserPoolAuthorizer validated `aud` against the client id and only id
 * tokens carry it. On Entra that reasoning inverts: an access token's `aud` is
 * the resource, which is what STAFF_AUDIENCE holds.
 *
 * It would still "work" by accident if we sent the id token, and that is
 * exactly why this is explicit. Client and resource are the same app
 * registration here, so an id token's `aud` is also the client id, and
 * auth.py — which checks signature, exp, iss, aud and roles, and nothing about
 * token type — would accept it. Relying on that would make the portal correct
 * by coincidence, and the coincidence ends the day the API gets its own
 * registration.
 */
export async function accessToken(): Promise<string> {
  if (!pca) throw new Error('configureAuth() was never called');
  const account = currentAccount();
  if (!account) throw new Error('not signed in');
  try {
    const result = await pca.acquireTokenSilent({ scopes: [scope], account });
    return result.accessToken;
  } catch (err: unknown) {
    if (err instanceof InteractionRequiredAuthError) {
      // The refresh token is gone or consent is newly required. Redirecting is
      // the only cure; the call below navigates away, so the throw is just
      // what keeps the caller from using an empty string in the meantime.
      await pca.acquireTokenRedirect({ scopes: [scope], account });
      throw new Error('signing in again');
    }
    throw err;
  }
}
